import React from "react";

function GenerosManifestoContent({ showTopBorder = true }) {
  const innerShellClass = showTopBorder
    ? "md:py-20 border-t border-gray-800 pb-16 md:pb-24"
    : "pt-8 md:pt-12 pb-16 md:pb-24";

  return (
    <section>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className={innerShellClass} data-aos="fade-right">
          <div className="max-w-3xl mx-auto">
            <h2
              className="text-4xl font-bold mb-8 text-primary-1 text-center font-barlow"
              data-aos="fade-up"
            >
              The Generos Manifesto
            </h2>

            <div className="text-3xl text-white text-center md:text-left space-y-8">
              <p data-aos="fade-up">
                <span className="font-bold">Generos</span>{" "}
                <span className="italic">
                  (pronounced like the virtue:{" "}
                  <span className="font-bold not-italic">generous</span>)
                </span>{" "}
                is a rebellion against a future where your worth is measured by
                how replaceable your labor is.
              </p>

              <p data-aos="fade-up">
                The old economy was built on a deal that was always breaking.
                You trade your time for a paycheck, and in return you get a thin
                sense of safety and a story about &ldquo;meaningful work.&rdquo;
                The same companies that preached that story have always been
                searching for cheaper labor. Artificial intelligence is simply the
                latest answer. The message is clear: if your value is labor, you
                are negotiating against something that will always undercut you.
              </p>

              <p
                className="text-3xl md:text-4xl font-bold text-primary-1"
                data-aos="fade-up"
              >
                We believe humanity&rsquo;s true value was never in labor. It
                was always in virtue.
              </p>

              <p data-aos="fade-up">
                Virtue cannot be automated. Empathy cannot be outsourced.
                Generosity cannot be replaced by a model. As AI takes more of
                the tasks, the code, and the careers, those virtues are no
                longer &ldquo;nice to have.&rdquo; They are the foundation of a
                sane economic system.
              </p>

              <p data-aos="fade-up">
                The machines are automating labor. Generos is here to automate
                the way you generate wealth.
              </p>

              <p data-aos="fade-up">
                In the old world, generosity was framed as loss. You give, you
                have less. In Generos, generosity is how you grow. Every gift is
                a seed. Every vault you unlock, every connection you create is
                recorded, honored, and amplified. Generosity here doesn&rsquo;t
                disappear. It compounds.
              </p>

              <p
                className="text-3xl md:text-4xl font-bold text-primary-1"
                data-aos="fade-up"
              >
                This is not generosity as gesture. This is generosity as engine.
              </p>

              <p data-aos="fade-up">
                The machines will keep optimizing away human labor. Fine. Let
                them have the factories, the spreadsheets, the rote tasks.
                Generos asks a different question:
              </p>

              <blockquote
                className="border-l-4 border-primary-1 pl-6 text-lilac-400 text-3xl italic font-semibold"
                data-aos="fade-up"
              >
                What if we used the same automation that ended the 9-to-5 to
                fund a new era of human purpose?
              </blockquote>

              <p data-aos="fade-up">
                <span className="font-bold text-primary-1">
                  This is the New Partnership.
                </span>{" "}
                You bring the why. Your agents handle the how. You provide the
                intent, the generosity, the courage to give first. Your agents
                are the infrastructure translating your virtue into durable
                economic power that works while you live your life.
              </p>

              <p data-aos="fade-up">
                In the legacy economy, the tools owned you. In Generos, you own
                the engine.
              </p>

              <p data-aos="fade-up">
                We are not waiting for governments or corporations to figure it
                out. We are not begging for a seat at a shrinking table. We are
                building a new table. On-chain. Transparent. Owned by the people
                who fuel it.
              </p>

              <p data-aos="fade-up">
                If the old economy says, &ldquo;Your worth is your
                output,&rdquo; we answer, &ldquo;Our worth is our impact.&rdquo;
                If the old economy says, &ldquo;Do more with less people,&rdquo;
                we answer, &ldquo;Do more with more generosity.&rdquo;
              </p>

              <p data-aos="fade-up">
                Every vault is a promise: if you show up for others, the system
                will show up for you.
              </p>

              <p data-aos="fade-up">
                As automation accelerates, the gap between those who own the
                engines and those fed into them will only widen. You can be
                automated out of a place in the world, or you can step into
                ownership of a new kind of engine.
              </p>

              <p data-aos="fade-up">Own the Engine of Generosity.</p>

              <p data-aos="fade-up">
                This is your invitation to refuse a future where you are
                disposable, and to build one where every generous act
                strengthens our community and our position within it.
              </p>

              <p
                className="text-3xl md:text-4xl font-bold text-primary-1 text-center mt-12"
                data-aos="fade-up"
              >
                Give first. Own forever.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default GenerosManifestoContent;
